import type {CSSProperties, ReactNode} from "react";
import {toDecimalFromMoney, type Money} from "../../../../../../lib/Money.ts";
import {MoneyLabel} from "../../../../../general/MoneyLabel.tsx";
import Checkbox from "../../../../../general/Checkbox.tsx";
import type {PresentationSourceType} from "../../../../../../generated/enums.ts";
import {SortableHeader} from "../BookingTableHeader.tsx";
import {sourceTypeLabel} from "../TypeBadge.tsx";
import type {SortableVatRow, VatRowSort, VatRowSortColumn} from "../../utils/invoiceVatRows.ts";

const DATE_WIDTH = "6.5rem";
const AMOUNT_WIDTH = "8rem";
const VAT_WIDTH = "7rem";
const STATUS_WIDTH = "11rem";

export const INDENT_REM = 1.25;

export function gridTemplate(showStatus: boolean, showInvoiceColumn: boolean): CSSProperties {
    const columns = ["1.75rem", "minmax(0,1fr)"];
    if (showInvoiceColumn) columns.push("7.5rem");
    columns.push(DATE_WIDTH, AMOUNT_WIDTH, AMOUNT_WIDTH, VAT_WIDTH);
    if (showStatus) columns.push(STATUS_WIDTH);
    return {gridTemplateColumns: columns.join(" ")};
}

export function AmountText({amount}: {amount: Money}) {
    if (amount.signed_amount === 0) return <span className="text-muted-foreground">-</span>;
    return (
        <span className="tabular-nums text-gray-800">
            <MoneyLabel value={toDecimalFromMoney(amount)} currency={amount.currency} showCurrency={true} />
        </span>
    );
}

export interface SelectAllState {
    allSelected: boolean;
    someSelected: boolean;
    disabled: boolean;
    onToggle: () => void;
}

export function VatRowHeader({
    sort,
    onSortChange,
    showStatus,
    showInvoiceColumn,
    selectAll,
}: {
    sort: VatRowSort;
    onSortChange: (column: VatRowSortColumn) => void;
    showStatus: boolean;
    showInvoiceColumn: boolean;
    selectAll: SelectAllState;
}) {
    return (
        <div
            className="sticky top-0 z-10 grid min-h-[2.5rem] items-center rounded-t-lg bg-white py-2 pl-3 shadow-[inset_0_-1px_0_0_#e5e7eb]"
            style={gridTemplate(showStatus, showInvoiceColumn)}
        >
            <span className="flex">
                <Checkbox
                    value={selectAll.allSelected}
                    indeterminate={selectAll.someSelected && !selectAll.allSelected}
                    disabled={selectAll.disabled}
                    onUpdateFunc={selectAll.onToggle}
                    ariaLabel="Selecteer alle boekingen"
                />
            </span>
            {showInvoiceColumn ? (
                <SortableHeader label="Omschrijving" column="relation" sort={sort} onSortChange={onSortChange} className="px-2" />
            ) : (
                <span className="px-2 text-xs font-medium text-muted-foreground">Omschrijving</span>
            )}
            {showInvoiceColumn && (
                <SortableHeader label="Factuurnr." column="invoice" sort={sort} onSortChange={onSortChange} className="px-2" />
            )}
            <SortableHeader label="Datum" column="date" sort={sort} onSortChange={onSortChange} className="px-2" />
            <SortableHeader
                label="Debet"
                column="debit"
                sort={sort}
                onSortChange={onSortChange}
                alignRight
                className="justify-self-end px-2"
            />
            <SortableHeader
                label="Credit"
                column="credit"
                sort={sort}
                onSortChange={onSortChange}
                alignRight
                className="justify-self-end px-2"
            />
            <SortableHeader label="Btw" column="vat" sort={sort} onSortChange={onSortChange} className="pl-2 pr-4" />
            {showStatus && (
                <span className="justify-self-end pr-3 text-xs font-medium text-muted-foreground">Status</span>
            )}
        </div>
    );
}

export function RelationCell({row}: {row: SortableVatRow & {kind: PresentationSourceType | null}}) {
    const kindLabel = row.kind && row.kind !== "BILL" ? sourceTypeLabel(row.kind) : null;
    const primary = row.relationName ?? row.title;
    const secondary = [kindLabel, primary !== row.title ? row.title : null]
        .filter((part): part is string => part !== null)
        .join(" · ");
    return (
        <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-gray-800">{primary}</span>
            {secondary && <span className="mt-0.5 block truncate text-xs text-muted-foreground">{secondary}</span>}
        </span>
    );
}

export function NestedBookings({children, showStatus}: {children: ReactNode; showStatus: boolean}) {
    return (
        <div
            className="border-t border-gray-100 py-1 pl-6"
            style={{paddingRight: showStatus ? STATUS_WIDTH : undefined}}
        >
            <table className="w-full table-fixed">
                <colgroup>
                    <col style={{width: "3.5rem"}} />
                    <col style={{width: "2rem"}} />
                    <col style={{width: "8rem"}} />
                    <col />
                    <col style={{width: DATE_WIDTH}} />
                    <col style={{width: AMOUNT_WIDTH}} />
                    <col style={{width: AMOUNT_WIDTH}} />
                    <col style={{width: VAT_WIDTH}} />
                </colgroup>
                <tbody className="[&>tr:last-child]:border-b-0">{children}</tbody>
            </table>
        </div>
    );
}
